import {
  ArrowLeftOutlined,
  DownloadOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons"
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Flex,
  Radio,
  Select,
  Skeleton,
  Tabs,
  Tag,
  Typography,
  notification
} from "antd"
import type { Dayjs } from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/routes.ts"
import { CalculationHelp } from "../CalculationHelp"
import { PerformanceLoadError } from "../api"
import { dateToPeriodKey, periodKeyToDate } from "../period-keys"
import type { PerformanceGrain } from "../types"
import { MilestonesView } from "./MilestonesView"
import { OverviewView } from "./OverviewView"
import { ProjectsView } from "./ProjectsView"
import { TypesView } from "./TypesView"
import {
  downloadPerformanceCsv,
  fetchMemberOverview,
  fetchMemberTypes,
  memberPaths,
  rangeParams
} from "./api"
import type {
  Compare,
  MemberOverviewResponse,
  MemberView,
  PatternLink
} from "./types"
import { useMemberUrlState } from "./use-member-url-state"

type Load =
  | { status: "loading" }
  | { status: "missing" }
  | { status: "error" }
  | { status: "ready"; data: MemberOverviewResponse }

const VIEWS: MemberView[] = ["overview", "projects", "types", "milestones"]
const COMPARES: Compare[] = ["company", "previous_period", "previous_year"]

export function MemberPerformancePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { memberId = "" } = useParams()
  const { query, view, metric, update } = useMemberUrlState()
  const [state, setState] = useState<Load>({ status: "loading" })
  const [attempt, setAttempt] = useState(0)
  const [helpOpen, setHelpOpen] = useState(false)
  const [typeOptions, setTypeOptions] = useState<
    { id: string; name: string }[]
  >([])
  const [drillLabels, setDrillLabels] = useState<{
    period?: string
    type?: string
    milestone?: string
  }>({})
  const overviewKey = JSON.stringify([
    query.grain,
    query.from,
    query.to,
    query.projectType,
    query.compare
  ])
  const rangeKey = JSON.stringify([query.grain, query.from, query.to])

  useEffect(() => {
    let current = true
    setState((prev) => (prev.status === "ready" ? prev : { status: "loading" }))
    fetchMemberOverview(memberId, query)
      .then((data) => current && setState({ status: "ready", data }))
      .catch((error) => {
        if (!current) return
        setState({
          status:
            error instanceof PerformanceLoadError && error.status === 404
              ? "missing"
              : "error"
        })
      })
    return () => {
      current = false
    }
  }, [memberId, overviewKey, attempt])

  useEffect(() => {
    let current = true
    fetchMemberTypes(memberId, query)
      .then(
        (body) =>
          current &&
          setTypeOptions(
            body.types.map((row) => ({ id: row.id, name: row.name }))
          )
      )
      .catch(() => current && setTypeOptions([]))
    return () => {
      current = false
    }
  }, [memberId, rangeKey])

  const data = state.status === "ready" ? state.data : undefined
  const from = query.from ?? data?.filters.from
  const to = query.to ?? data?.filters.to

  const drill = (link: PatternLink) => {
    const label = (key?: string) =>
      data?.trend.find((p) => p.period === key)?.label
    setDrillLabels({
      period: label(link.period),
      type: typeOptions.find((o) => o.id === link.type)?.name,
      milestone: data?.patterns.find((p) => p.link.milestone === link.milestone)
        ?.params.milestone as string | undefined
    })
    update({
      view: link.view,
      projectType: link.type ?? query.projectType ?? null,
      milestone: link.milestone ?? null,
      period: link.period ?? null,
      flag: null
    })
  }

  // Opened from the Comparison tab: Back returns to that comparison, carrying
  // only its own keys.
  const back = searchParams.get("back")
  const backParams = back ? new URLSearchParams(back) : null
  const fromComparison = backParams?.get("tab") === "comparison"

  const backToTeam = () => {
    if (backParams && fromComparison) {
      const keep = new URLSearchParams({ tab: "comparison" })
      backParams.forEach((value, name) => {
        if (name.startsWith("cmp_")) keep.append(name, value)
      })
      navigate(`${RoutesPaths.team_management}?${keep.toString()}`)
      return
    }
    const params = new URLSearchParams({
      tab: "performance",
      grain: query.grain
    })
    if (to) params.set("period", to)
    const role = searchParams.get("role")
    if (role) params.set("role", role)
    if (query.projectType) params.set("type", query.projectType)
    navigate(`${RoutesPaths.team_management}?${params.toString()}`)
  }

  const failed = () =>
    notification.error({ message: t("teamPerformance.exportFailed") })

  const exportCsv = () => {
    const name = data?.member.name ?? "member"
    if (view === "projects") {
      downloadPerformanceCsv(
        memberPaths.projects(memberId),
        {
          ...rangeParams({ ...query, from, to }),
          ...(query.period ? { period: query.period } : {}),
          ...(query.milestone ? { milestone: query.milestone } : {}),
          ...(query.flag ? { flag: query.flag } : {})
        },
        `performance-projects-${name}.csv`
      ).catch(failed)
    } else {
      downloadPerformanceCsv(
        memberPaths.overview(memberId),
        { ...rangeParams({ ...query, from, to }), compare: query.compare },
        `performance-trend-${name}.csv`
      ).catch(failed)
    }
  }

  if (state.status === "missing") {
    return (
      <Empty description={t("teamPerformance.member.notFound")}>
        <Button onClick={backToTeam}>
          {fromComparison
            ? t("teamPerformance.member.backToComparison")
            : t("teamPerformance.member.backToTeam")}
        </Button>
      </Empty>
    )
  }

  const picker = (
    value: string | undefined,
    onChange: (key: string) => void,
    label: string
  ) => (
    <DatePicker
      aria-label={label}
      picker={query.grain}
      allowClear={false}
      value={periodKeyToDate(query.grain, value)}
      format={
        query.grain === "quarter"
          ? "YYYY-[Q]Q"
          : query.grain === "year"
            ? "YYYY"
            : "MMM YYYY"
      }
      onChange={(d: Dayjs | null) =>
        d && onChange(dateToPeriodKey(query.grain, d))
      }
    />
  )

  return (
    <Flex vertical gap="middle" className="size-full overflow-auto">
      <Flex justify="space-between" align="flex-start" wrap gap="small">
        <Flex vertical>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            className="w-fit px-0"
            onClick={backToTeam}
          >
            {fromComparison
              ? t("teamPerformance.member.backToComparison")
              : t("teamPerformance.member.backToTeam")}
          </Button>
          <Typography.Title level={2} className="!mb-0 text-start">
            {data?.member.name ?? " "}
          </Typography.Title>
          {data && (
            <Flex gap={8} align="center">
              <Typography.Text type="secondary">
                {t(`teamPerformance.roles.${data.member.role}`)} ·{" "}
                {t("teamPerformance.member.subtitle")}
              </Typography.Text>
              {!data.member.is_active && (
                <Tag>{t("teamPerformance.table.inactive")}</Tag>
              )}
            </Flex>
          )}
        </Flex>
        <Flex gap="small">
          <Button
            icon={<QuestionCircleOutlined />}
            onClick={() => setHelpOpen(true)}
          >
            {t("teamPerformance.help.button")}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportCsv}
            disabled={!data}
          >
            {t("teamPerformance.export")}
          </Button>
        </Flex>
      </Flex>

      <Flex gap="middle" wrap align="flex-end">
        <Flex vertical gap={2}>
          <Typography.Text type="secondary" className="text-xs">
            {t("teamPerformance.filters.grain")}
          </Typography.Text>
          <Select<PerformanceGrain>
            aria-label={t("teamPerformance.filters.grain")}
            className="min-w-32"
            value={query.grain}
            onChange={(grain) => update({ grain })}
            options={(["month", "quarter", "year"] as const).map((g) => ({
              value: g,
              label: t(`teamPerformance.grains.${g}`)
            }))}
          />
        </Flex>
        <Flex vertical gap={2}>
          <Typography.Text type="secondary" className="text-xs">
            {t("teamPerformance.member.range")}
          </Typography.Text>
          <Flex gap={4} align="center">
            {picker(
              from,
              (key) => update({ from: key, to: to && key > to ? key : to }),
              t("teamPerformance.member.rangeFrom")
            )}
            <span>–</span>
            {picker(
              to,
              (key) =>
                update({ to: key, from: from && key < from ? key : from }),
              t("teamPerformance.member.rangeTo")
            )}
          </Flex>
        </Flex>
        <Flex vertical gap={2}>
          <Typography.Text type="secondary" className="text-xs">
            {t("teamPerformance.filters.projectType")}
          </Typography.Text>
          <Select<string>
            aria-label={t("teamPerformance.filters.projectType")}
            className="min-w-40"
            value={query.projectType ?? ""}
            onChange={(value) => update({ projectType: value || null })}
            options={[
              { value: "", label: t("teamPerformance.filters.allTypes") },
              ...typeOptions.map((o) => ({
                value: o.id,
                label:
                  o.id === "unknown"
                    ? t("teamPerformance.filters.unknownType")
                    : o.name
              }))
            ]}
          />
        </Flex>
        <Flex vertical gap={2}>
          <Typography.Text type="secondary" className="text-xs">
            {t("teamPerformance.member.compareLabel")}
          </Typography.Text>
          <div
            role="radiogroup"
            aria-label={t("teamPerformance.member.compareLabel")}
          >
            <Radio.Group
              optionType="button"
              value={query.compare}
              onChange={(e) => update({ compare: e.target.value as Compare })}
              options={COMPARES.map((c) => ({
                value: c,
                label: t(`teamPerformance.member.compare.${c}`)
              }))}
            />
          </div>
        </Flex>
      </Flex>

      <Tabs
        activeKey={view}
        onChange={(key) => update({ view: key as MemberView })}
        items={VIEWS.map((key) => ({
          key,
          label: t(`teamPerformance.member.views.${key}`)
        }))}
      />

      {state.status === "error" && (
        <Alert
          type="error"
          showIcon
          message={t("teamPerformance.states.loadFailed")}
          action={
            <Button onClick={() => setAttempt((n) => n + 1)}>
              {t("teamPerformance.states.retry")}
            </Button>
          }
        />
      )}
      {state.status === "loading" && view === "overview" && (
        <Skeleton active paragraph={{ rows: 8 }} />
      )}
      {data && view === "overview" && (
        <OverviewView
          data={data}
          metric={metric}
          onMetricChange={(m) => update({ metric: m })}
          onDrill={drill}
        />
      )}
      {view === "projects" && (
        <ProjectsView
          memberId={memberId}
          query={{ ...query, from, to }}
          labels={{
            period: drillLabels.period,
            type:
              drillLabels.type ??
              typeOptions.find((o) => o.id === query.projectType)?.name,
            milestone: drillLabels.milestone
          }}
          onChange={update}
        />
      )}
      {view === "types" && (
        <TypesView
          memberId={memberId}
          query={{ ...query, from, to }}
          onOpenType={(row) => drill({ view: "projects", type: row.id })}
        />
      )}
      {view === "milestones" && (
        <MilestonesView
          memberId={memberId}
          query={{ ...query, from, to }}
          onMinChange={(min) => update({ min })}
          onOpenMilestone={(row) => {
            setDrillLabels({ milestone: row.label })
            update({
              view: "projects",
              milestone: row.key,
              period: null,
              flag: null
            })
          }}
        />
      )}
      <CalculationHelp
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        thresholds={data?.thresholds}
        versions={data?.score_versions}
      />
    </Flex>
  )
}
